import { DependencyList, useCallback, useEffect, useState } from "react";

/**
 * Feathers the edges of a scroll container so long lists don't cut off with a
 * hard line. Returns a callback `ref` to attach to the scroll element and a
 * `maskImage` that fades whichever edges can still be scrolled toward.
 */
export function useScrollFade(deps: DependencyList = []) {
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });

  useEffect(() => {
    if (!node) return;

    const update = () => {
      const canScroll = node.scrollHeight > node.clientHeight + 1;
      const top = canScroll && node.scrollTop > 1;
      const bottom =
        canScroll && node.scrollTop + node.clientHeight < node.scrollHeight - 1;
      setFade((prev) =>
        prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
      );
    };

    update();
    node.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(node);
    // Also watch the content wrapper so height changes (e.g. an accordion
    // expanding) recompute the fade even when the container size is fixed.
    if (node.firstElementChild) observer.observe(node.firstElementChild);

    return () => {
      node.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, [node, ...deps]);

  const ref = useCallback((el: HTMLElement | null) => setNode(el), []);

  const maskImage = `linear-gradient(to bottom, ${
    fade.top ? "transparent, black 16px" : "black 0"
  }, ${fade.bottom ? "black calc(100% - 16px), transparent" : "black 100%"})`;

  return { ref, maskImage };
}
